import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SidePanel from '../../components/SidePanel/SidePanel';
import Dashboard from '../../components/HomeComponents/Dashboard';
import Incubations from '../../components/HomeComponents/Incubations';
import DashboardAccess from '../../components/HomeComponents/DashboardAccess';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import { getUserAccess, getUserIncubationAccess, getCurrentUserProfile } from '../../controllers/users';
import './HomeScreen.css';

const HomeScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  const {
    clearUserData,
    setGlobalsFromApi,
    setIncubationAccessFromApi,
    currentUser,
    setCurrentUser,
    globalFunctionalities,
    hasGlobalFunctionality
  } = useUser();

  const hasDashboardAccess = hasGlobalFunctionality('admin_dashboard') || hasGlobalFunctionality('manage_dashboard');
  
  
  // Get the active section from sessionStorage, default to 'Dashboard'
  const [activeItem, setActiveItem] = useState(() => {
    const savedSection = sessionStorage.getItem('activeSection');
    return savedSection || 'Dashboard';
  });

  // Check for navigation state and update active section accordingly
  useEffect(() => {
    if (location.state?.activeSection) {
      setActiveItem(location.state.activeSection);
      // Clear the state to prevent it from persisting
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Redirect users away from restricted sections if they don't have access
  useEffect(() => {
    const restrictedSections = ['Dashboard Access'];
    if (restrictedSections.includes(activeItem) && !hasDashboardAccess) {
      setActiveItem('Dashboard');
    }
  }, [activeItem, hasDashboardAccess]);

  // Save the active section to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('activeSection', activeItem);
  }, [activeItem]);

  // Fetch fresh user access data whenever HomeScreen mounts/refreshes
  useEffect(() => {
    const fetchUserAccessData = async () => {
      // Check if we have a token (user is logged in)
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      try {
        // First, get the current user profile using the JWT token (no parameters needed)
        const userProfileResponse = await getCurrentUserProfile();
        
        if (userProfileResponse.data && userProfileResponse.data.success && userProfileResponse.data.user_profile) {
          const userProfile = userProfileResponse.data.user_profile;
          
          // Update current user context with the profile data
          setCurrentUser({
            user_profile_id: userProfile.id,
            email: userProfile.email,
            first_name: userProfile.first_name,
            last_name: userProfile.last_name,
            // Add any other fields you need
          });

          const [userAccessResponse, incubationAccessResponse] = await Promise.all([
            getUserAccess({ user_profile_id: userProfile.id }).catch(() => null),
            getUserIncubationAccess({ user_profile_id: userProfile.id }).catch(() => null)
          ]);

          if (userAccessResponse?.data?.success && userAccessResponse.data.userAccess) {
            setGlobalsFromApi(userAccessResponse.data.userAccess);
          }
          if (incubationAccessResponse?.data?.success) {
            setIncubationAccessFromApi(incubationAccessResponse.data);
          }
        }
      } catch (error) {
        // Don't show error to user as this is a background operation
      }
    };

    fetchUserAccessData();
  }, []); // Empty dependency array means this runs once when component mounts

  const handleNavClick = (section) => {
    if (section === 'Sign Out') {
      // Clear user data from context and localStorage
      clearUserData();
      // Clear auth token from localStorage
      localStorage.removeItem('token');
      showSuccess("Successfully signed out!");
      navigate('/');
    } else if (section === 'Settings') {
      navigate('/settings');
    } else {
      setActiveItem(section);
      // Navigation logic for other items will be implemented here
    }
  };

  const renderActiveComponent = () => {
    switch (activeItem) {
      case 'Dashboard':
        return <Dashboard />;
      case 'Incubations':
        return <Incubations />;
      case 'Dashboard Access':
        return <DashboardAccess />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="home-screen">
      <SidePanel activeItem={activeItem} onNavClick={handleNavClick} globalFunctionalities={globalFunctionalities} />
      <div className="home-content">
        {renderActiveComponent()}
      </div>
    </div>
  );
};

export default HomeScreen;